import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES } from "@/lib/site";

export const Services = ({ onQuoteClick }) => {
  const [house, driveway, gutter] = SERVICES;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32 bg-slate-50 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12 md:mb-16">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">
            What we do
          </p>
          <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Three services. <span className="text-blue-600">Done right.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 font-medium">
            We're not a generalist crew. We focus on three things and we do them
            better than anyone in Ohio.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          <ServiceCard
            service={house}
            className="md:col-span-8 md:row-span-2"
            featured
            onQuoteClick={onQuoteClick}
          />
          <ServiceCard
            service={driveway}
            className="md:col-span-4"
            onQuoteClick={onQuoteClick}
          />
          <ServiceCard
            service={gutter}
            className="md:col-span-4"
            onQuoteClick={onQuoteClick}
          />
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, className = "", featured = false, onQuoteClick }) => (
  <article
    data-testid={`service-card-${service.id}`}
    className={`group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col ${className}`}
  >
    <div
      className={`relative overflow-hidden ${
        featured ? "aspect-[16/10]" : "aspect-[4/3]"
      }`}
    >
      <img
        src={service.image}
        alt={service.title}
        className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
      <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] font-bold uppercase tracking-wider text-slate-700">
        {service.short}
      </span>
    </div>

    <div className={`p-6 md:p-8 flex flex-col flex-1 ${featured ? "md:p-10" : ""}`}>
      <h3
        className={`font-display font-extrabold tracking-tight text-slate-900 ${
          featured ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
        }`}
      >
        {service.title}
      </h3>
      <p className="mt-2 text-slate-600 font-medium leading-relaxed text-sm md:text-base">
        {service.description}
      </p>

      <ul className="mt-5 grid grid-cols-2 gap-2">
        {service.bullets.map((b) => (
          <li key={b} className="flex items-center gap-2 text-xs md:text-sm text-slate-700 font-semibold">
            <span className="h-5 w-5 rounded-full bg-blue-50 text-blue-600 grid place-items-center shrink-0">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {b}
          </li>
        ))}
      </ul>

      <button
        data-testid={`service-quote-${service.id}`}
        onClick={onQuoteClick}
        className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 self-start group/btn"
      >
        Get a quote for {service.title.toLowerCase()}
        <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
      </button>
    </div>
  </article>
);
